// optimistic.ts - 낙관적 갱신(optimistic update) 재계산.
//
// 담당자가 [완료] 를 누르면 서버 응답을 기다리지 않고 화면이 즉시 바뀌어야
// 한다 (§2 "1건당 1분 이내", §04 §3 인터랙션 표). 서버는 마일스톤 1건만
// 돌려주므로 진행률·롤업 상태·D-Day·현재단계는 웹이 다시 계산한다.
//
// 여기가 `status.ts` TS 미러의 존재 이유다. 서버가 정본이고 이 파일은
// "다음 새로고침까지의 예측"이므로, 두 계산이 어긋나면 화면이 깜빡인다.
// 그래서 §03 §10 표준 케이스를 양쪽이 공유한다.
import type { DevItemDetail, Milestone, PrepItem, ProductionPlan } from "@/lib/api";
import {
  DEFAULT_SETTINGS,
  type JudgeSettings,
  dDay,
  formatDDay,
  itemStatus,
  milestoneStatus,
  prepStatus,
  prepWarning,
  progressPct,
  resolveActiveIndex,
  sopForecast,
} from "@/lib/status";

/** 계획일이 하나라도 등록되어 있는가 (itemStatus 의 I2 판정 입력). */
const anyPlanned = (ms: readonly Milestone[], preps: readonly PrepItem[]): boolean =>
  ms.some((m) => !!m.plannedDate) || preps.some((p) => !!p.requiredDate || !!p.expectedDate);

/**
 * 하위 항목의 날짜가 바뀐 뒤 파생값 전체를 다시 계산한다.
 * `asOf`(서버가 판정한 기준일)를 그대로 쓴다 - 로컬 시계를 새로 읽으면
 * 자정 근처에서 서버와 어긋난다.
 */
export function recomputeDetail(
  detail: DevItemDetail,
  settings: JudgeSettings = DEFAULT_SETTINGS
): DevItemDetail {
  const today = detail.asOf;
  const activeIndex = resolveActiveIndex(detail.milestones);

  const milestones = detail.milestones.map((m, i) => ({
    ...m,
    isActive: i === activeIndex,
    status: milestoneStatus(
      m.plannedDate,
      m.completedDate,
      i === activeIndex,
      today,
      settings.imminentDays
    ),
  }));

  const prepItems = detail.prepItems.map((p) => ({
    ...p,
    status: prepStatus(
      p.requiredDate,
      p.expectedDate,
      p.completedDate,
      today,
      settings.imminentDays
    ),
    warning: prepWarning(p.requiredDate, p.expectedDate, p.completedDate, today),
  }));

  const sop = milestones.find((m) => m.key === "sop");
  const sopCompleted = !!sop?.completedDate;

  const status = itemStatus(
    {
      milestoneStatuses: milestones.map((m) => m.status),
      prepStatuses: prepItems.map((p) => p.status),
      hasPlannedDates: anyPlanned(milestones, prepItems),
      sopDate: detail.sopDate,
      sopCompleted,
    },
    today,
    settings.sopImminentDays
  );

  const days = dDay(detail.sopDate, today);
  const active = activeIndex === null ? null : milestones[activeIndex];

  return {
    ...detail,
    milestones,
    prepItems,
    progress: progressPct(milestones, activeIndex, settings.progressWeights),
    status,
    dDay: days,
    dDayLabel: formatDDay(days),
    sopForecast: sopForecast(status),
    currentStage: active ? { key: active.key, label: active.label } : null,
    nextTask: nextTask(milestones, detail.productionPlans),
  };
}

/**
 * 가장 이른 미완료 마일스톤 / 생산계획 (REQ-DETAIL-006).
 * 계획일이 없는 항목은 후보가 아니다.
 */
export function nextTask(
  milestones: readonly Milestone[],
  plans: readonly ProductionPlan[]
): DevItemDetail["nextTask"] {
  const candidates: { date: string; label: string; kind: string; targetId: string }[] = [];
  for (const m of milestones) {
    if (m.completedDate || !m.plannedDate) continue;
    candidates.push({ date: m.plannedDate, label: m.label, kind: "milestone", targetId: m.id });
  }
  for (const p of plans) {
    if (p.isDone || !p.plannedDate) continue;
    candidates.push({
      date: p.plannedDate,
      label: `생산 ${p.qty}EA`,
      kind: "production",
      targetId: p.id,
    });
  }
  if (candidates.length === 0) return null;
  candidates.sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));
  return candidates[0];
}

/** 마일스톤 1건을 바꿔 끼우고 파생값을 다시 계산한다. */
export function withMilestone(
  detail: DevItemDetail,
  id: string,
  patch: Partial<Pick<Milestone, "plannedDate" | "completedDate">>,
  settings?: JudgeSettings
): DevItemDetail {
  return recomputeDetail(
    {
      ...detail,
      milestones: detail.milestones.map((m) => (m.id === id ? { ...m, ...patch } : m)),
    },
    settings
  );
}

/** 준비항목 1건을 바꿔 끼우고 파생값을 다시 계산한다. */
export function withPrepItem(
  detail: DevItemDetail,
  id: string,
  patch: Partial<Pick<PrepItem, "requiredDate" | "expectedDate" | "completedDate">>,
  settings?: JudgeSettings
): DevItemDetail {
  return recomputeDetail(
    {
      ...detail,
      prepItems: detail.prepItems.map((p) => (p.id === id ? { ...p, ...patch } : p)),
    },
    settings
  );
}

/** 생산계획 1건을 바꿔 끼운다. 생산계획은 롤업 상태에 들어가지 않는다 (§03 §3-1). */
export function withProductionPlan(
  detail: DevItemDetail,
  id: string,
  patch: Partial<Pick<ProductionPlan, "isDone" | "doneDate">>,
  settings?: JudgeSettings
): DevItemDetail {
  return recomputeDetail(
    {
      ...detail,
      productionPlans: detail.productionPlans.map((p) => (p.id === id ? { ...p, ...patch } : p)),
    },
    settings
  );
}
